import { createApi } from '@reduxjs/toolkit/query/react';
import { createBackendBaseQuery, transformApiError } from '../../utils/createAuthBaseQuery';
import { API_URLS } from '../../constants/apiUrl';

export interface UserSearchData {
  totalResults: number;
  users: UserData[];
}

// User data type matching the API response
export interface UserData {
  id: number;
  email: string;
  firstName: string;
  lastName: string;
  regionIds: number;
  roleId: number;
  secondaryRoleId: number;
  createdAtUtc: string;
  createdBy: string;
  updatedAtUtc: string | null;
  updatedBy: string | null;
  region: string;
  role: string;
  secondaryRole: string;
  userName: string;
  status: string;
  moduleName: string;
}

// Query params for pagination, sorting, and filtering
export interface FetchUsersParams {
  page?: number;
  limit?: number;
  sortBy?: keyof UserData;
  order?: 'asc' | 'desc';
  [key: string]: string | number | undefined;
}

// API response wrapper
export interface ApiResponse<T> {
  data: T;
  success: boolean;
  message?: string;
  total?: number;
}

const BASE_URL = process.env.REACT_APP_USER_API_BASE_URL as string;

if (!BASE_URL) {
  throw new Error('REACT_APP_USER_API_BASE_URL environment variable is not set');
}

// Create the base query using the reusable factory. Deleting a user can return a 403 for a
// resource-specific reason (e.g. the caller doesn't manage one of that user's modules/regions),
// which the component shows inline — it shouldn't bounce the whole app to /unauthorized.
const baseQuery = createBackendBaseQuery(BASE_URL, undefined, true, 10000, ['fetchDeleteUserById']);

export const userApiSlice = createApi({
  reducerPath: 'userApi',
  baseQuery: baseQuery,
  tagTypes: ['User'],
  endpoints(builders) {
    return {
      fetchUsers: builders.query<ApiResponse<UserSearchData>, FetchUsersParams | void>({
        query: (params) => {
          let queryString = '';
          if (params) {
            const usp = new URLSearchParams();
            Object.entries(params).forEach(([key, value]) => {
              if (value !== undefined && value !== null && value !== '') {
                usp.append(key, String(value));
              }
            });
            queryString = usp.toString() ? `?${usp.toString()}` : '';
          }
          return {
            url: `${API_URLS.USER_MANAGEMENT.SEARCH}${queryString}`,
            method: 'GET',
          };
        },
        providesTags: ['User'],
        transformErrorResponse: transformApiError,
      }),
      
      fetchUserById: builders.query<ApiResponse<UserData>, number>({
        query: (id) => ({
          url: `${API_URLS.USER_MANAGEMENT.SEARCH}?userId=${id}`,
          method: 'GET',
        }),
        providesTags: (result, error, id) => [{ type: 'User', id }],
        transformErrorResponse: transformApiError,
      }),
      
      fetchDeleteUserById: builders.mutation<ApiResponse<UserData>, number>({
        query: (id) => ({
          url: `${API_URLS.USER_MANAGEMENT.DELETE_BY_ID}?userId=${id}`,
          method: 'DELETE',
        }),
        invalidatesTags: (result, error, id) => [{ type: 'User', id }, 'User'],
        transformErrorResponse: transformApiError,
      }),

      // New endpoints with better error handling
      createUser: builders.mutation<ApiResponse<UserData>, Partial<UserData>>({
        query: (userData) => ({
          url: API_URLS.USER_MANAGEMENT.CREATE_USER,
          method: 'POST',
          body: userData,
        }),
        invalidatesTags: ['User'],
        transformErrorResponse: transformApiError,
      }),

      updateUser: builders.mutation<ApiResponse<UserData>, { id: number; userData: Partial<UserData> }>({
        query: ({ id, userData }) => ({
          url: `${API_URLS.USER_MANAGEMENT.CREATE_USER}/${id}`,
          method: 'PUT',
          body: userData,
        }),
        invalidatesTags: (result, error, { id }) => [{ type: 'User', id }, 'User'],
        transformErrorResponse: transformApiError,
      }),
    };
  },
});

export const {
  useFetchUsersQuery,
  useFetchUserByIdQuery,
  useFetchDeleteUserByIdMutation,
  useCreateUserMutation,
  useUpdateUserMutation,
  // Export hooks for lazy queries
  useLazyFetchUsersQuery,
  useLazyFetchUserByIdQuery,
} = userApiSlice;
